import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { IapScope, ORGANIZATION } from '../ICSConstants';

export type Action = ActionBase & {
  id: UUID;
  group_guid: UUID;
  timestamp: Date;
  status?: string;
};

export const keys = {
  base: 'actions',
  get all() {
    return [keys.base];
  },
  list: (group_guid: UUID, user_guid: UUID) => [
    keys.base,
    group_guid,
    user_guid,
  ],
} as const;

export const useActions = (scope?: IapScope) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<Action[]>(
        `${config.apiGateway.ics_actions}/${group_guid}`
      );
      return resp.data;
    },
    enabled: scope === ORGANIZATION,
  });
};

export type CreateActionReqData = ActionBase & {
  action_id?: UUID;
  incident_id?: UUID;
  user_guid?: UUID;
  poc?: string | null;
  briefed_poc?: boolean;
  start_date?: Date | null;
  status?: string | null;
  target_date?: Date | null;
  actual_date?: Date | null;
  resources?: UUID[];
  parent_id?: UUID | null;
  utc_offset?: number;
};

export type CreateActionResponse = {
  archived: boolean;
  checklist: string[];
  description: string;
  id: UUID;
  group_guid: UUID;
  name: string;
  resources: UUID[];
  timestamp: Date;
};

export const useCreateAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateActionReqData) => {
      return await axios.post<CreateActionResponse>(
        `${config.apiGateway.ics_actions}`,
        {
          ...data,
          group_guid,
          user_guid,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export type EditActionReqData = ActionBase & {
  id: UUID;
};

export const useEditAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditActionReqData) => {
      return await axios.put(`${config.apiGateway.ics_actions}/${data.id}`, {
        ...data,
        group_guid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
